import { Platform } from 'react-native';
import { GoogleAuthProvider, signInWithCredential, updateProfile } from 'firebase/auth';
import * as Application from 'expo-application';
import * as AuthSession from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import Constants from 'expo-constants';
import { auth } from '@/lib/firebase';
import { AccountLinkingRequiredError, getEmailFromIdToken, getNameFromIdToken } from './accountLinking';

// Complete the auth session for web
if (Platform.OS === 'web') {
  WebBrowser.maybeCompleteAuthSession();
}

const GOOGLE_DISCOVERY_URL = 'https://accounts.google.com';

/** Discovery document for Google OAuth. Prefetch on sign-in screen mount to avoid delay when user taps "Continue with Google". */
let cachedDiscovery: AuthSession.DiscoveryDocument | null = null;

/**
 * Prefetch Google OAuth discovery document. Call when the sign-in screen mounts so the first tap opens the browser faster.
 */
export function prefetchGoogleDiscovery(): void {
  if (Platform.OS === 'web') return;
  if (!getGoogleClientId()) return;
  AuthSession.fetchDiscoveryAsync(GOOGLE_DISCOVERY_URL)
    .then((d) => {
      cachedDiscovery = d;
    })
    .catch(() => {
      cachedDiscovery = null;
    });
}

function readGoogleIdsFromExpoExtra(): { ios?: string; android?: string; web?: string } {
  const extra = Constants.expoConfig?.extra as Record<string, unknown> | undefined;
  if (!extra) return {};
  const str = (k: string) => (typeof extra[k] === 'string' ? (extra[k] as string) : undefined);
  return {
    ios: str('EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID'),
    android: str('EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID'),
    web: str('EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID'),
  };
}

/**
 * OAuth client IDs for Google sign-in via expo-auth-session (authorization code + PKCE).
 *
 * iOS: iOS OAuth client (supports `com.googleusercontent.apps.<id>:/oauthredirect`).
 *
 * Android: Android OAuth client with redirect `{applicationId}:/oauthredirect` (package name as scheme).
 * Matches expo-auth-session's Google provider — not the iOS reversed-client-id scheme.
 * Google disables custom URI schemes on Android clients by default (2024+). In Google Cloud Console,
 * open the Android OAuth client → Advanced settings → enable "Custom URI scheme", then wait a few minutes.
 * Do **not** use the Web client with a custom scheme; Google returns "Custom scheme URIs are not allowed for 'WEB' client type."
 *
 * Env order mirrors runtime precedence; `app.config.js` mirrors these into expo.extra when env is missing from JS.
 */
export function getGoogleClientId(): string | undefined {
  const fromExtra = readGoogleIdsFromExpoExtra();
  if (Platform.OS === 'ios') {
    return (
      process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID ||
      fromExtra.ios ||
      process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ||
      fromExtra.web
    );
  }
  if (Platform.OS === 'android') {
    return (
      process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID ||
      fromExtra.android ||
      process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ||
      fromExtra.web
    );
  }
  return process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID || fromExtra.web;
}

/** True when the active client ID is the Web OAuth client (custom scheme redirect is invalid for it). */
function isWebGoogleClientId(clientId: string): boolean {
  const fromExtra = readGoogleIdsFromExpoExtra();
  const webId =
    process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID || fromExtra.web;
  return Boolean(webId && clientId === webId);
}

export function isGoogleSignInConfigured(): boolean {
  // Web uses Firebase popup and does not require client IDs here.
  if (Platform.OS === 'web') return true;
  return !!getGoogleClientId();
}

function getRedirectUri(): string {
  const clientId = getGoogleClientId();

  // Android + Android OAuth client: package name redirect (expo-auth-session Google provider).
  if (Platform.OS === 'android') {
    if (clientId && !isWebGoogleClientId(clientId) && Application.applicationId) {
      return `${Application.applicationId}:/oauthredirect`;
    }
    return AuthSession.makeRedirectUri({
      scheme: 'tripvio',
      path: 'google-callback',
    });
  }

  // iOS + iOS OAuth client: reversed client id scheme (CFBundleURLTypes in app.config.js).
  if (!clientId) {
    return AuthSession.makeRedirectUri({
      scheme: 'tripvio',
      path: 'google-callback',
    });
  }
  if (isWebGoogleClientId(clientId)) {
    return AuthSession.makeRedirectUri({
      scheme: 'tripvio',
      path: 'google-callback',
    });
  }
  const suffix = '.apps.googleusercontent.com';
  if (!clientId.endsWith(suffix)) {
    return AuthSession.makeRedirectUri({
      scheme: 'tripvio',
      path: 'google-callback',
    });
  }
  const clientIdPart = clientId.slice(0, -suffix.length);
  const reversed = `com.googleusercontent.apps.${clientIdPart}`;
  return `${reversed}:/oauthredirect`;
}

/**
 * Sign in with Google using Firebase Authentication
 * Native: Uses Google OAuth directly via expo-auth-session and exchanges the ID token with Firebase.
 * Web: Uses Firebase popup sign-in.
 */
export async function signInWithGoogle(): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      const { signInWithPopup } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const u = cred.user;
      if (u && !u.displayName?.trim()) {
        try {
          const token = await u.getIdToken();
          const fromToken = getNameFromIdToken(token);
          if (fromToken) {
            await updateProfile(u, { displayName: fromToken });
          }
        } catch (profileErr) {
          console.warn('[GoogleAuth] updateProfile fallback (web) failed:', profileErr);
        }
      }
      return;
    }

    const clientId = getGoogleClientId();
    if (!clientId) {
      const envName =
        Platform.OS === 'ios'
          ? 'EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID (or EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID)'
          : 'EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID (or EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID)';
      throw new Error(
        `Google sign-in is not configured for this build. Set ${envName} in your EAS profile env vars.`,
      );
    }

    const redirectUri = getRedirectUri();
    const discovery =
      cachedDiscovery ?? (await AuthSession.fetchDiscoveryAsync(GOOGLE_DISCOVERY_URL));
    cachedDiscovery = discovery;

    // Use an explicit AuthRequest so we don't rely on Firebase's /__/auth/handler (breaks on iOS due to storage partitioning).
    // Use authorization code + PKCE and exchange for tokens.
    const request = new AuthSession.AuthRequest({
      clientId,
      redirectUri,
      responseType: AuthSession.ResponseType.Code,
      scopes: ['openid', 'profile', 'email'],
      usePKCE: true,
      extraParams: {
        // Helps ensure user can pick an account.
        prompt: 'select_account',
      },
    });

    const result = await request.promptAsync(discovery, {
      // Prefer a shared session so Google can reuse existing login state.
      // If you want "always fresh", flip this to true.
      preferEphemeralSession: false,
    });

    if (result.type === 'cancel' || result.type === 'dismiss') {
      throw new Error('Google sign-in was cancelled');
    }
    if (result.type !== 'success') {
      throw new Error(`Google sign-in failed: ${result.type}`);
    }

    let idToken = (result as any).params?.id_token as string | undefined;
    const authorizationCode = (result as any).params?.code as string | undefined;
    if (!idToken && authorizationCode) {
      const tokenResponse = await AuthSession.exchangeCodeAsync(
        {
          clientId,
          code: authorizationCode,
          redirectUri,
          extraParams: request.codeVerifier ? { code_verifier: request.codeVerifier } : undefined,
        },
        discovery,
      );
      idToken =
        (tokenResponse as any).idToken ||
        ((tokenResponse as any).params?.id_token as string | undefined);
    }
    if (!idToken) {
      // Helpful debug context in dev
      if (__DEV__) {
        // Avoid logging full tokens; just list param keys.
        const keys = Object.keys(((result as any).params || {}) as Record<string, unknown>);
        console.warn('[GoogleAuth] Missing id_token. Params keys:', keys);
        console.warn('[GoogleAuth] Expo extra appEnv:', (Constants.expoConfig?.extra as any)?.appEnv);
      }
      throw new Error('Google sign-in completed but no ID token was returned.');
    }

    const credential = GoogleAuthProvider.credential(idToken);
    try {
      await signInWithCredential(auth, credential);
    } catch (signInError: unknown) {
      const err = signInError as { code?: string; customData?: { email?: string } };
      if (err?.code === 'auth/credential-already-in-use') {
        const email =
          (err.customData?.email as string | undefined)?.trim() ||
          getEmailFromIdToken(idToken) ||
          '';
        if (email) {
          throw new AccountLinkingRequiredError(email, credential, 'Google');
        }
      }
      throw signInError;
    }

    const userAfterSignIn = auth.currentUser;
    if (userAfterSignIn && !userAfterSignIn.displayName?.trim()) {
      const fromToken = getNameFromIdToken(idToken);
      if (fromToken) {
        try {
          await updateProfile(userAfterSignIn, { displayName: fromToken });
        } catch (profileErr) {
          console.warn('[GoogleAuth] updateProfile fallback failed (sign-in still succeeded):', profileErr);
        }
      }
    }
  } catch (error) {
    if (error instanceof AccountLinkingRequiredError) throw error;
    console.error('[GoogleAuth] Sign-in error:', error);
    if (error instanceof Error) {
      if (error.message.includes('cancel')) throw error;
      throw error;
    }
    throw new Error('Failed to sign in with Google. Please try again.');
  }
}

